import { useMutation } from '@tanstack/react-query';
import { Form } from 'antd';
import type { FormInstance } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { useCallback, useMemo } from 'react';
import { ApiError } from '../../api';

/**
 * The one hook every form uses. Owns antd `<Form>` state, tanstack mutation,
 * server-error extraction, and field-error mapping. See .claude/FORMS.md §
 * What `useVForm` is.
 */

export interface FieldError {
  name: NamePath;
  errors: string[];
}

export interface UseVFormOptions<TValues extends object, TResult> {
  initial?: Partial<TValues>;
  mutation: (values: TValues) => Promise<TResult>;
  onSuccess?: (result: TResult, values: TValues) => void;
  /**
   * Turn a mutation error into per-field errors. Return an empty array to
   * fall through to the top-level `serverError` string.
   */
  mapServerError?: (err: unknown) => FieldError[];
}

export interface VFormApi<TValues extends object, TResult> {
  form: FormInstance<TValues>;
  submit: () => void;
  reset: () => void;
  isSubmitting: boolean;
  serverError: string | null;
  result: TResult | undefined;
  /** Spread onto `<Form>` — provides `form`, `layout`, `initialValues`, `onFinish`. */
  formProps: {
    form: FormInstance<TValues>;
    layout: 'vertical';
    initialValues: Partial<TValues> | undefined;
    onFinish: (values: TValues) => void;
  };
}

export function useVForm<TValues extends object, TResult = unknown>(
  options: UseVFormOptions<TValues, TResult>,
): VFormApi<TValues, TResult> {
  const [form] = Form.useForm<TValues>();

  const mutation = useMutation({
    mutationFn: options.mutation,
    onSuccess: (result, values) => {
      options.onSuccess?.(result, values);
    },
    onError: (err) => {
      const fieldErrors = options.mapServerError?.(err) ?? [];
      if (fieldErrors.length > 0) {
        form.setFields(fieldErrors.map((fe) => ({ name: fe.name, errors: fe.errors })));
      }
    },
  });

  const submit = useCallback(() => {
    form.submit();
  }, [form]);

  const reset = useCallback(() => {
    form.resetFields();
    mutation.reset();
  }, [form, mutation]);

  const serverError = useMemo(() => {
    if (!mutation.error) return null;
    // If the caller mapped this to field errors, don't also show it up top.
    const mapped = options.mapServerError?.(mutation.error) ?? [];
    if (mapped.length > 0) return null;
    return extractServerMessage(mutation.error);
  }, [mutation.error, options]);

  return {
    form,
    submit,
    reset,
    isSubmitting: mutation.isPending,
    serverError,
    result: mutation.data,
    formProps: {
      form,
      layout: 'vertical',
      initialValues: options.initial,
      onFinish: (values: TValues) => mutation.mutate(values),
    },
  };
}

function extractServerMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body?.message) return body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
